import { api, PERM_LIST, type AdminAction, type AdminUser, type Perm, type Status, type TemplateCategory, type TemplateInfo } from '../sync/api';
import { thDateTime } from '../time';
import { $ } from './dom';
import { initBgAdminCard } from './bgAdminCard';
import { initFreeImportCard } from './freeImportCard';
import { initPrintProfileCard } from './printProfileCard';
import { toast } from './toast';

/*
 * Admin back office (full screen over the editor):
 *   ผู้ใช้  — approve / reject (= disable) waiting accounts, disable, enable, make admin or member;
 *            extra rights for an active member, one tick each (0011): templates, AI music, music upload
 *   ตั้งค่า — colour profile for print-shop PDFs (src/ui/printProfileCard.ts); ลบพื้นหลัง (src/ui/bgAdminCard.ts);
 *            คลังกราฟิก ชุดฟรี (src/ui/freeImportCard.ts, 0019);
 *            how many days a sign-in lasts; sign everyone else out
 * Every refusal from the server is shown with its reason; risky actions need a second tap.
 */

const ACTION_TH: Record<AdminAction, string> = {
  approve: 'อนุมัติเป็นสมาชิก', disable: 'ปิดการใช้งาน', enable: 'เปิดใช้งานอีกครั้ง', make_admin: 'ตั้งเป็นผู้ดูแล', make_member: 'เปลี่ยนเป็นสมาชิก'
};
const DONE_TH: Record<AdminAction, string> = {
  approve: 'อนุมัติแล้ว', disable: 'ปิดการใช้งานแล้ว (ออกจากระบบทุกเครื่องแล้ว)', enable: 'เปิดใช้งานแล้ว', make_admin: 'ตั้งเป็นผู้ดูแลแล้ว', make_member: 'เปลี่ยนเป็นสมาชิกแล้ว'
};
const NEEDS_SECOND_TAP: AdminAction[] = ['disable', 'make_admin', 'make_member'];

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};
const errText = (e: unknown) => e instanceof Error ? e.message : String(e);
const when = (iso: string | null) => iso ? thDateTime(new Date(iso)) : '-';

export function initAdminPanel(opts: { myId: () => string | undefined; onChanged: () => void }) {
  const root = $('admin');
  let tab: 'users' | 'templates' | 'settings' = 'users';
  let filter: Status = 'pending';
  let users: AdminUser[] = [];
  let armed: string | null = null;          // `${userId}:${action}` waiting for the second tap
  let armTimer: ReturnType<typeof setTimeout> | undefined;
  let busy = false;

  const usersMsg = $('adUsersMsg'), list = $('adList');
  const setMsg = (box: HTMLElement, text: string, ok = false) => {
    box.textContent = text; box.hidden = !text; box.classList.toggle('ok', ok);
  };

  /* ---------- tabs ---------- */
  function showTab(t: typeof tab) {
    tab = t;
    $('adTabUsers').setAttribute('aria-selected', String(t === 'users'));
    $('adTabSettings').setAttribute('aria-selected', String(t === 'settings'));
    $('adTabTemplates').setAttribute('aria-selected', String(t === 'templates'));
    $('adUsers').hidden = t !== 'users';
    $('adSettings').hidden = t !== 'settings';
    $('adTemplates').hidden = t !== 'templates';
    if (t === 'users') void loadUsers(); else if (t === 'templates') void loadTemplates(); else void loadSettings();
  }
  $('adTabUsers').onclick = () => showTab('users');
  $('adTabSettings').onclick = () => showTab('settings');
  $('adTabTemplates').onclick = () => showTab('templates');
  $('adBack').onclick = () => { root.hidden = true; opts.onChanged(); };

  /* ---------- users ---------- */
  async function loadUsers() {
    setMsg(usersMsg, '');
    list.textContent = '';
    list.append(el('p', 'empty', 'กำลังโหลด…'));
    try {
      users = (await api.admin.users()).users;
      if (!users.some(u => u.status === filter)) {
        filter = users.some(u => u.status === 'pending') ? 'pending' : 'active';
      }
      renderUsers();
    } catch (e) {
      list.textContent = '';
      setMsg(usersMsg, 'โหลดรายชื่อผู้ใช้ไม่สำเร็จ: ' + errText(e));
    }
  }

  function renderUsers() {
    const count = (s: Status) => users.filter(u => u.status === s).length;
    const chips = $('adFilters');
    chips.textContent = '';
    for (const [s, label] of [['pending', 'รออนุมัติ'], ['active', 'ใช้งาน'], ['disabled', 'ปิดใช้']] as [Status, string][]) {
      const b = el('button', 'fchip', `${label} ${count(s)}`);
      b.setAttribute('aria-pressed', String(filter === s));
      b.onclick = () => { filter = s; armed = null; renderUsers(); };
      chips.append(b);
    }
    list.textContent = '';
    const shown = users.filter(u => u.status === filter);
    if (!shown.length) {
      list.append(el('p', 'empty', filter === 'pending' ? 'ไม่มีคำขอที่รออนุมัติ' : 'ไม่มีผู้ใช้ในกลุ่มนี้'));
      return;
    }
    shown.forEach(u => list.append(card(u)));
  }

  function card(u: AdminUser) {
    const c = el('article', 'ucard');
    const head = el('div', 'uhead');
    const av = el('span', 'avatar', (u.name || u.email).charAt(0).toUpperCase());
    const who = el('div', 'uwho');
    who.append(el('b', '', u.name || u.email.split('@')[0]), el('small', '', `${u.email}${u.status !== 'pending' ? ` · งาน ${u.designs} ชิ้น` : ''}`));
    head.append(av, who);
    if (u.status !== 'pending') head.append(el('span', u.role === 'admin' ? 'rchip admin' : 'rchip', u.role === 'admin' ? 'ผู้ดูแล' : 'สมาชิก'));
    c.append(head);

    const facts: string[] = [];
    if (u.status === 'pending') facts.push(`ขอใช้งานเมื่อ ${when(u.createdAt)}`);
    else if (u.status === 'active') facts.push(`อนุมัติ ${when(u.approvedAt)}${u.approvedBy ? ` โดย ${u.approvedBy}` : ''}`, `ใช้ล่าสุด ${when(u.lastLoginAt)}`);
    else facts.push(`ปิดการใช้งาน ${when(u.statusAt)}${u.statusBy ? ` โดย ${u.statusBy}` : ''}`);
    if (u.fixedAdmin) facts.push('อยู่ใน ADMIN_EMAILS (ผู้ดูแลถาวร)');
    c.append(el('small', 'ufacts', facts.join(' · ')));

    const isMe = u.id === opts.myId();
    if (isMe) { c.append(el('small', 'ufacts', 'บัญชีของคุณ เปลี่ยนสถานะหรือสิทธิ์ของตัวเองไม่ได้')); return c; }

    const actions: AdminAction[] =
      u.status === 'pending' ? ['approve', 'disable'] :
      u.status === 'active' ? [u.role === 'admin' ? 'make_member' : 'make_admin', 'disable'] :
      ['enable'];
    const row = el('div', 'uacts');
    for (const a of actions) {
      const key = `${u.id}:${a}`;
      const label = u.status === 'pending' && a === 'disable' ? 'ปฏิเสธ' : ACTION_TH[a];
      const b = el('button', a === 'approve' || a === 'enable' ? 'btn main' : a === 'disable' ? 'btn danger' : 'btn',
        armed === key ? `แตะอีกครั้งเพื่อ${label}` : label) as HTMLButtonElement;
      b.disabled = busy;
      b.onclick = () => void act(u, a, key);
      row.append(b);
    }
    c.append(row);
    if (u.status === 'active' && u.role !== 'admin') c.append(rights(u));
    else if (u.status === 'active') c.append(el('small', 'ufacts', 'ผู้ดูแลทำได้ทุกอย่าง ไม่ต้องให้สิทธิ์เพิ่ม'));
    return c;
  }

  /* ---------- extra rights of a member (0011) ---------- */
  function rights(u: AdminUser): HTMLElement {
    const box = el('div', 'uperms');
    box.append(el('small', 'ufacts', 'สิทธิ์เพิ่มเติม'));
    for (const p of PERM_LIST) {
      const given = u.permInfo?.find(g => g.perm === p.perm);
      const label = el('label', 'pcheck');
      const cb = document.createElement('input'); cb.type = 'checkbox';
      cb.checked = !!given; cb.disabled = busy;
      cb.onchange = () => void setPerm(u, p.perm, cb);
      const text = el('span', '', p.name);
      text.append(el('small', 'ufacts', ` — ${p.hint}${given ? ` · ให้เมื่อ ${when(given.at)} โดย ${given.by}` : ''}`));
      label.append(cb, text);
      box.append(label);
    }
    return box;
  }
  async function setPerm(u: AdminUser, perm: Perm, cb: HTMLInputElement) {
    const on = cb.checked, name = PERM_LIST.find(p => p.perm === perm)!.name;
    // keep the tick the admin just made while the server answers (BUG-2): only this box is locked, nothing is redrawn
    cb.disabled = true;
    try {
      await api.admin.setPerm(u.id, perm, on);
      const done = `${u.email}: ${on ? 'ให้' : 'ถอน'}สิทธิ์${name}แล้ว`;
      setMsg(usersMsg, done, true); toast(done);
    } catch (e) {
      setMsg(usersMsg, `${u.email}: ${on ? 'ให้' : 'ถอน'}สิทธิ์${name}ไม่สำเร็จ: ${errText(e)}`);
      toast(errText(e), true);
    } finally {
      await loadUsersKeepMsg();   // the list as the server has it now (a refused tick goes back)
    }
  }

  async function act(u: AdminUser, a: AdminAction, key: string) {
    if (NEEDS_SECOND_TAP.includes(a) && armed !== key) {
      armed = key; clearTimeout(armTimer);
      armTimer = setTimeout(() => { armed = null; renderUsers(); }, 4000);
      renderUsers(); return;
    }
    armed = null; busy = true; renderUsers();
    try {
      await api.admin.act(u.id, a);
      const label = u.status === 'pending' && a === 'disable' ? 'ปฏิเสธแล้ว' : DONE_TH[a];
      setMsg(usersMsg, `${u.email}: ${label}`, true);
      toast(`${u.email}: ${label}`);
    } catch (e) {
      setMsg(usersMsg, `${u.email}: ${errText(e)}`);
      toast(errText(e), true);
    } finally {
      busy = false;
      await loadUsersKeepMsg();
      opts.onChanged();
    }
  }

  /** Reload after an action without clearing the result message. */
  async function loadUsersKeepMsg() {
    try { users = (await api.admin.users()).users; renderUsers(); }
    catch (e) { setMsg(usersMsg, 'โหลดรายชื่อผู้ใช้ใหม่ไม่สำเร็จ: ' + errText(e)); }
  }

  /* ---------- templates and their categories ---------- */
  const tplMsg = $('adTplMsg'), catList = $('adCats'), tplList = $('adTpls');
  let cats: TemplateCategory[] = [];
  let tpls: TemplateInfo[] = [];
  let armedTpl: string | null = null;      // `${kind}:${id}` waiting for the second tap (delete)
  let tplBusy = false;

  async function loadTemplates(keepMsg = false) {
    if (!keepMsg) setMsg(tplMsg, '');
    try {
      const r = await api.admin.templates();
      cats = r.categories; tpls = r.templates;
      renderTemplates();
    } catch (e) { setMsg(tplMsg, 'โหลดเทมเพลตไม่สำเร็จ: ' + errText(e)); }
  }

  async function tplAction(label: string, fn: () => Promise<unknown>, okText: string) {
    if (tplBusy) return;
    tplBusy = true; renderTemplates();
    try { await fn(); setMsg(tplMsg, okText, true); }
    catch (e) { setMsg(tplMsg, `${label}ไม่สำเร็จ: ${errText(e)}`); }
    finally { tplBusy = false; armedTpl = null; await loadTemplates(true); }
  }

  function needsSecondTap(key: string): boolean {
    if (armedTpl === key) return false;
    armedTpl = key;
    setTimeout(() => { if (armedTpl === key) { armedTpl = null; renderTemplates(); } }, 4000);
    renderTemplates();
    return true;
  }

  function smallBtn(text: string, cls = 'btn') {
    const b = el('button', cls, text) as HTMLButtonElement;
    b.disabled = tplBusy;
    return b;
  }

  function renderTemplates() {
    catList.textContent = '';
    for (const c of cats) {
      const row = el('div', 'crow');
      const name = el('span', 'cname', c.name);
      name.append(el('small', '', ` · ${c.count}`));
      const ren = smallBtn('เปลี่ยนชื่อ');
      ren.onclick = () => {
        const input = el('input') as HTMLInputElement;
        input.type = 'text'; input.value = c.name; input.maxLength = 40; input.setAttribute('aria-label', `ชื่อใหม่ของหมวด ${c.name}`);
        const save = smallBtn('บันทึก', 'btn main');
        save.onclick = () => void tplAction('เปลี่ยนชื่อหมวด', () => api.admin.renameCategory(c.id, input.value), `เปลี่ยนชื่อหมวดเป็น "${input.value.trim()}" แล้ว`);
        row.replaceChildren(input, save);
        input.focus();
      };
      row.append(name, ren);
      if (c.count === 0) {
        const key = `cat:${c.id}`;
        const del = smallBtn(armedTpl === key ? 'แตะอีกครั้งเพื่อลบ' : 'ลบ', 'btn danger');
        del.onclick = () => { if (!needsSecondTap(key)) void tplAction('ลบหมวด', () => api.admin.deleteCategory(c.id), `ลบหมวด "${c.name}" แล้ว`); };
        row.append(del);
      }
      catList.append(row);
    }

    tplList.textContent = '';
    if (!tpls.length) { tplList.append(el('p', 'empty', 'ยังไม่มีเทมเพลต เพิ่มได้จากเมนู "เพิ่มเติม" ของงาน → บันทึกเป็นเทมเพลต')); return; }
    for (const t of tpls) {
      const card = el('div', 'tplrow' + (t.hidden ? ' hiddenrow' : ''));
      const img = el('img') as HTMLImageElement;
      img.src = t.thumb; img.alt = ''; img.loading = 'lazy';
      const info = el('div', 'uwho');
      const catName = cats.find(c => c.id === t.categoryId)?.name ?? '-';
      info.append(el('b', '', t.name + (t.hidden ? ' (ซ่อนอยู่)' : '')),
        el('small', '', `${catName} · ใช้ ${t.uses} ครั้ง · ${when(t.updatedAt)}${t.updatedByName ? ` โดย ${t.updatedByName}` : ''}`));
      const sel = el('select') as HTMLSelectElement;
      sel.setAttribute('aria-label', `หมวดของเทมเพลต ${t.name}`);
      for (const c of cats) sel.append(new Option(c.name, c.id, false, c.id === t.categoryId));
      sel.disabled = tplBusy;
      sel.onchange = () => void tplAction('ย้ายหมวด', () => api.admin.patchTemplate(t.id, { categoryId: sel.value }), `ย้าย "${t.name}" ไปหมวด "${sel.selectedOptions[0]?.text}" แล้ว`);
      const hide = smallBtn(t.hidden ? 'แสดง' : 'ซ่อน');
      hide.onclick = () => void tplAction(t.hidden ? 'แสดงเทมเพลต' : 'ซ่อนเทมเพลต', () => api.admin.patchTemplate(t.id, { hidden: !t.hidden }),
        t.hidden ? `แสดง "${t.name}" ให้สมาชิกเห็นแล้ว` : `ซ่อน "${t.name}" แล้ว สมาชิกจะไม่เห็น`);
      const key = `tpl:${t.id}`;
      const del = smallBtn(armedTpl === key ? 'แตะอีกครั้งเพื่อลบ' : 'ลบ', 'btn danger');
      del.onclick = () => { if (!needsSecondTap(key)) void tplAction('ลบเทมเพลต', () => api.admin.deleteTemplate(t.id), `ลบเทมเพลต "${t.name}" แล้ว งานที่สร้างจากเทมเพลตนี้ไม่หาย`); };
      const acts = el('div', 'uacts');
      acts.append(sel, hide, del);
      card.append(img, info, acts);
      tplList.append(card);
    }
  }

  const catIn = $('adCatName') as HTMLInputElement;
  $('adCatAdd').onclick = () => void tplAction('เพิ่มหมวด', () => api.admin.addCategory(catIn.value), `เพิ่มหมวด "${catIn.value.trim()}" แล้ว`).then(() => {
    if (tplMsg.classList.contains('ok')) catIn.value = '';
  });

  /* ---------- settings ---------- */
  const printProfiles = initPrintProfileCard();
  const bgCard = initBgAdminCard();
  const freeCard = initFreeImportCard();
  const daysIn = $('adDays') as HTMLInputElement, setMsgBox = $('adSetMsg'), saveBtn = $('adSave') as HTMLButtonElement;
  let armedRevoke = false;

  async function loadSettings() {
    void printProfiles.load();
    void bgCard.load();
    void freeCard.load();
    setMsg(setMsgBox, '');
    try {
      const s = await api.admin.settings();
      daysIn.value = String(s.sessionDays);
      $('adSetInfo').textContent = s.stored && s.updatedAt ? `แก้ล่าสุด ${when(s.updatedAt)} โดย ${s.updatedBy}` : 'ยังไม่เคยแก้ ใช้ค่าเริ่มต้น';
      markPresets();
    } catch (e) { setMsg(setMsgBox, 'โหลดการตั้งค่าไม่สำเร็จ: ' + errText(e)); }
  }
  function markPresets() {
    $('adPresets').querySelectorAll<HTMLButtonElement>('button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.days === daysIn.value.trim())));
  }
  $('adPresets').querySelectorAll<HTMLButtonElement>('button').forEach(b => {
    b.onclick = () => { daysIn.value = b.dataset.days!; markPresets(); };
  });
  daysIn.oninput = markPresets;

  saveBtn.onclick = async () => {
    const raw = daysIn.value.trim();
    const n = Number(raw);
    if (!raw || !Number.isInteger(n) || n < 1 || n > 90) { setMsg(setMsgBox, 'ต้องเป็นจำนวนเต็ม 1 ถึง 90 วัน ยังไม่ได้บันทึก'); return; }
    saveBtn.disabled = true;
    try {
      const s = await api.admin.saveSettings(n);
      setMsg(setMsgBox, `บันทึกแล้ว: อยู่ในระบบได้ ${s.sessionDays} วัน มีผลกับการเข้าสู่ระบบครั้งถัดไป`, true);
      $('adSetInfo').textContent = `แก้ล่าสุด ${when(s.updatedAt)} โดย ${s.updatedBy}`;
    } catch (e) { setMsg(setMsgBox, 'บันทึกไม่สำเร็จ: ' + errText(e)); }
    finally { saveBtn.disabled = false; }
  };

  const revokeBtn = $('adRevoke') as HTMLButtonElement, revokeMsg = $('adRevokeMsg');
  revokeBtn.onclick = async () => {
    if (!armedRevoke) {
      armedRevoke = true; revokeBtn.textContent = 'แตะอีกครั้งเพื่อให้ทุกเครื่องออกจากระบบ';
      setTimeout(() => { armedRevoke = false; revokeBtn.textContent = 'ออกจากระบบทุกเครื่อง'; }, 4000);
      return;
    }
    armedRevoke = false; revokeBtn.textContent = 'ออกจากระบบทุกเครื่อง'; revokeBtn.disabled = true;
    try {
      const r = await api.admin.revokeAll();
      setMsg(revokeMsg, `ให้ออกจากระบบแล้ว ${r.removed} การเข้าสู่ระบบ (ยกเว้นเครื่องนี้)`, true);
    } catch (e) { setMsg(revokeMsg, 'ทำรายการไม่สำเร็จ: ' + errText(e)); }
    finally { revokeBtn.disabled = false; }
  };

  return {
    open() { root.hidden = false; showTab(tab); }
  };
}
